import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of, startWith, switchMap, take } from 'rxjs';

import { RelatorioCustoService } from '../shared/service/relatorio-custo.service';
import { ViewMode, AnoCusto, MesCusto } from '../shared/model/relatorio-custo.models';

import { RelatorioHeaderComponent } from './relatorio-header/relatorio-header.component';
import { MesesListaComponent } from './meses-lista/meses-lista.component';
import { VisaoRankingComponent } from './visao-ranking/visao-ranking.component';
import { VisaoMensalComponent } from './visao-mensal/visao-mensal.component';
import { VisaoAnualComponent } from './visao-anual/visao-anual.component';
import { BackfillEventosCustoService } from '../shared/service/BackfillEventosCusto.service';

interface AnoRequestState { loading: boolean; data: AnoCusto }
interface MesRequestState { loading: boolean; data: MesCusto | null }

@Component({
  selector: 'app-relatorios-centro-custo',
  standalone: true,
  imports: [
    CommonModule,
    RelatorioHeaderComponent,
    MesesListaComponent,
    VisaoRankingComponent,
    VisaoMensalComponent,
    VisaoAnualComponent,
  ],
  templateUrl: './relatorios-centro-custo.component.html',
  styleUrls: ['./relatorios-centro-custo.component.css'],
})
export class RelatoriosCentroCustoComponent {
  private readonly custoService = inject(RelatorioCustoService);
  private readonly backfillService = inject(BackfillEventosCustoService);
  private readonly refreshDados = signal(0);

  importando = signal(false);

  anoSelecionado = signal<number>(new Date().getFullYear());
  viewMode = signal<ViewMode>('GRAFICOS');
  mesSelecionado = signal<number | null>(null);
  metaToggle = signal<boolean>(false);

  anosDisponiveis = toSignal(this.custoService.getAnosDisponiveis$(), { initialValue: [] });
  anoRequestState = toSignal(
    toObservable(computed(() => ({
      ano: this.anoSelecionado(),
      refresh: this.refreshDados(),
    }))).pipe(
      switchMap(({ ano }) =>
        this.custoService.getAno$(ano).pipe(
          map((data) => ({ loading: false, data: data ?? { ano, meses: [] } })),
          catchError(() => of<AnoRequestState>({ loading: false, data: { ano, meses: [] } })),
          startWith<AnoRequestState>({ loading: true, data: { ano, meses: [] } })
        )
      )
    ),
    { initialValue: { loading: true, data: { ano: this.anoSelecionado(), meses: [] } } }
  );
  mesRequestState = toSignal(
    toObservable(computed(() => ({
      ano: this.anoSelecionado(),
      mes: this.mesSelecionado(),
      refresh: this.refreshDados(),
    }))).pipe(
      switchMap(({ ano, mes }) => {
        if (mes == null) {
          return of<MesRequestState>({ loading: false, data: null });
        }

        return this.custoService.getMes$(ano, mes).pipe(
          map((data) => ({ loading: false, data: data ?? null })),
          catchError(() => of<MesRequestState>({ loading: false, data: null })),
          startWith<MesRequestState>({ loading: true, data: null })
        );
      })
    ),
    { initialValue: { loading: false, data: null } }
  );
  anoData = computed(() => this.anoRequestState().data);
  mesData = computed(() => this.mesRequestState().data);
  carregandoAno = computed(() => this.anoRequestState().loading);
  carregandoMes = computed(() => this.mesRequestState().loading);
  carregandoRanking = computed(() =>
    this.mesSelecionado() != null ? this.carregandoMes() : this.carregandoAno()
  );

  totalAnual = computed(() =>
    (this.anoData().meses ?? []).reduce((acc, m) => acc + (m.total ?? 0), 0)
  );

  constructor() {
    effect(() => {
      const anos = this.anosDisponiveis();
      if (!anos.length) return;

      const selecionado = this.anoSelecionado();

      if (anos.includes(selecionado)) return;

      const current = new Date().getFullYear();
      const anoInicial = anos.includes(current) ? current : anos[0];

      this.anoSelecionado.set(anoInicial);
    }, { allowSignalWrites: true });

    effect(() => {
      this.anoSelecionado();
      this.mesSelecionado.set(null);
    }, { allowSignalWrites: true });
  }

  onAnoChange(ano: number) { this.anoSelecionado.set(ano); }
  onViewModeChange(mode: ViewMode) { this.viewMode.set(mode); }
  onMesSelect(mes: number | null) { this.mesSelecionado.set(mes); }
  onMetaToggleChange(value: boolean) { this.metaToggle.set(value); }

  private refreshDashboardData() {
    this.refreshDados.update((value) => value + 1);
  }


  importarEventosHistoricos(dataInicio?: string, dataFim?: string) {
    if (this.importando()) return;

    this.importando.set(true);

    this.backfillService.importarGrupo(dataInicio, dataFim).pipe(take(1)).subscribe({
      next: (res) => {
        console.log('✅ Backfill concluído:', res);

        this.custoService.refreshAnos();
        this.refreshDashboardData();

        this.importando.set(false);

        alert(`Importação concluída! ${res.eventosImportados} eventos importados.`);
      },
      error: (e) => {
        console.error('❌ Erro ao importar:', e);
        this.importando.set(false);
        alert('Erro ao importar eventos históricos.');
      },
    });
  }

  importarUltimoMes() {
    if (this.importando()) return;

    this.importando.set(true);

    this.backfillService.importarUltimoMes().pipe(take(1)).subscribe({
      next: (res) => {
        console.log('✅ Importação do último mês concluída:', res);

        this.custoService.refreshAnos();
        this.refreshDashboardData();

        this.importando.set(false);

        alert(`Sincronizado! ${res.eventosImportados} eventos do último mês.`);
      },
      error: (e) => {
        console.error('❌ Erro ao sincronizar:', e);
        this.importando.set(false);
      },
    });
  }
}
